import {
  DateFilterModel,
  ISimpleFilterModel,
  NumberFilterModel,
  SetFilterModel,
  TextFilterModel,
} from 'ag-grid-community';
import {
  IBooleanBlotterFilter,
  IDateBlotterFilter,
  INumberBlotterFilter,
  ISetBlotterFilter,
  ITextBlotterFilter,
  ServerSideBlotterSearchRequest,
} from 'apiConnector/blotters';
import { FilterTranslator } from 'components/shared/Blotter/Grid/Hooks/requestTranslator';
import { format, parseISO } from 'date-fns';
import { first } from 'lodash-es';

export type FilterItem = Exclude<ServerSideBlotterSearchRequest['filters'], undefined>[number];

const dateTranslator: () => FilterTranslator<DateFilterModel, FilterItem> = () => ({
  filterType: 'date',
  translate: (filterModel, colId) => {
    if (colId === undefined) {
      throw new Error('missing colId for filter');
    }
    const dateFrom = toDateOnly(filterModel.dateFrom ?? null) ?? undefined;
    const dateTo = toDateOnly(filterModel.dateTo ?? null) ?? undefined;
    switch (filterModel.type) {
      case 'inRange':
        return {
          field: colId,
          type: 'date',
          operation: 'inRange',
          dateTo,
          dateFrom,
        } satisfies IDateBlotterFilter;
      case 'greaterThan':
      case 'equals':
        return {
          field: colId,
          type: 'date',
          operation: filterModel.type,
          dateFrom,
        } satisfies IDateBlotterFilter;
      case 'lessThan':
        return {
          field: colId,
          type: 'date',
          operation: filterModel.type,
          dateFrom,
        } satisfies IDateBlotterFilter;
      case 'blank':
      case 'notBlank':
        return {
          field: colId,
          type: 'date',
          operation: filterModel.type,
        } satisfies IDateBlotterFilter;
      default:
        throw new Error(`Date filter '${filterModel.type}' is not supported`) as never;
    }
  },
});
const setTranslator: () => FilterTranslator<SetFilterModel, FilterItem> = () => ({
  filterType: 'set',
  translate: (filterModel, colId) => {
    if (colId === undefined) {
      throw new Error('missing colId for filter');
    }

    return {
      field: colId,
      type: 'set',
      operation: 'contains',
      joinOperator: 'OR',
      values: filterModel.values.map(s => s ?? ''),
    } satisfies ISetBlotterFilter;
  },
});

const booleanTranslator: () => FilterTranslator<SetFilterModel, FilterItem> = () => ({
  filterType: 'boolean',
  translate: (filterModel, colId) => {
    if (colId === undefined) {
      throw new Error('missing colId for filter');
    }
    if (filterModel.values.length !== 1) {
      return null;
    }
    return {
      field: colId,
      type: 'boolean',
      operation: 'equals',
      filter: first(filterModel.values),
    } as IBooleanBlotterFilter;
  },
});

const textTranslator: () => FilterTranslator<TextFilterModel, FilterItem> = () => ({
  filterType: 'text',
  translate: (filterModel, colId) => {
    if (colId === undefined) {
      throw new Error('missing colId for filter');
    }
    switch (filterModel.type) {
      case 'equals':
      case 'contains':
      case 'startsWith':
      case 'endsWith':
        return {
          field: colId,
          type: 'text',
          operation: filterModel.type,
          filter: filterModel.filter!,
        } satisfies ITextBlotterFilter;
      case 'blank':
      case 'notBlank':
        return {
          field: colId,
          type: 'text',
          operation: filterModel.type,
        } satisfies ITextBlotterFilter;
      default:
        throw new Error(`Text filter '${filterModel.type}' is not supported`);
    }
  },
});

const numberTranslator: () => FilterTranslator<NumberFilterModel, FilterItem> = () => ({
  filterType: 'number',
  translate: (filterModel, colId) => {
    if (colId === undefined) {
      throw new Error('missing colId for filter');
    }
    switch (filterModel.type) {
      case 'equals':
      case 'contains':
      case 'lessThan':
      case 'greaterThan':
        return {
          field: colId,
          type: 'number',
          operation: filterModel.type,
          filter: filterModel.filter!,
        } satisfies INumberBlotterFilter;
      case 'inRange':
        return {
          field: colId,
          type: 'number',
          operation: filterModel.type,
          filter: filterModel.filter!,
          filterTo: filterModel.filterTo!,
        } satisfies INumberBlotterFilter;
      case 'blank':
      case 'notBlank':
        return {
          field: colId,
          type: 'number',
          operation: filterModel.type,
        } satisfies INumberBlotterFilter;
      default:
        throw new Error(`Number filter '${filterModel.type}' is not supported`);
    }
  },
});

function toDateOnly(isoDate: string | null) {
  if (isoDate === null) {
    return null;
  }
  const date = parseISO(isoDate);
  return format(date, 'yyyy-MM-dd');
}

const allTranslators = [dateTranslator(), textTranslator(), numberTranslator(), booleanTranslator(), setTranslator()];

export function useServerSideTranslators() {
  return allTranslators as FilterTranslator<ISimpleFilterModel, FilterItem>[];
}
